// compare-logic — the PURE decisions of the "Compare algorithms" surface (compare-activity,
// Trello m98B2mq7). No DOM, no runner, no fetch: `compare-chrome.js` owns those and only
// executes what these functions decide. Pinned by the host-only
// `test/editor/compare-logic.test.js`.
//
// NOT named compare-grading: that is the SERVER file (`lib/util/compare-grading.js`). The
// equality rules below are a deliberate MIRROR of the server's (staff and preview evaluate the
// badges locally and post nothing); both specs are driven by the one shared fixture table
// `test/fixtures/compare-equality-cases.json`, so the two cannot drift apart silently.
//
// Student copy produced here stays in plain words — never any notation for how fast something
// grows (a source-scan spec over the string literals pins that).

export var ROWS = ['correctness', 'clarity', 'efficiency', 'suitability'];
export var SALVAGE_BUDGET_MS = 30000;
export var TOO_MANY = 'too many to count';

// ── equality (mirrors lib/util/compare-grading.js) ─────────────────────────────────────────

export function valuesEqual(a, b) {
  if (typeof a === 'number' && typeof b === 'number') {
    if (!isFinite(a) || !isFinite(b)) { return false; }
    return a === b;
  }
  if (a === null || b === null) { return a === b; }
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) { return false; }
    for (var i = 0; i < a.length; i++) {
      if (!valuesEqual(a[i], b[i])) { return false; }
    }
    return true;
  }
  if (typeof a === 'object' && typeof b === 'object') {
    var ka = Object.keys(a).sort(), kb = Object.keys(b).sort();
    if (ka.length !== kb.length) { return false; }
    for (var j = 0; j < ka.length; j++) {
      if (ka[j] !== kb[j] || !valuesEqual(a[ka[j]], b[kb[j]])) { return false; }
    }
    return true;
  }
  return typeof a === typeof b && a === b;
}

export function normaliseStdout(s) {
  var lines = String(s == null ? '' : s).replace(/\r\n?/g, '\n').split('\n')
    .map(function (l) { return l.replace(/\s+$/, ''); });
  while (lines.length && lines[lines.length - 1] === '') { lines.pop(); }
  return lines.join('\n');
}

export function stdoutEqual(a, b) {
  if (typeof a !== 'string' || typeof b !== 'string') { return false; }
  return normaliseStdout(a) === normaliseStdout(b);
}

function depthOf(value, d) {
  if (value && typeof value === 'object') {
    var max = d;
    var kids = Array.isArray(value) ? value : Object.keys(value).map(function (k) { return value[k]; });
    for (var i = 0; i < kids.length; i++) {
      var k = depthOf(kids[i], d + 1);
      if (k > max) { max = k; }
      if (max > 6) { return max; }
    }
    return max;
  }
  return d;
}

/** The value a check reports: serialisable, ≤ 2000 characters, nested ≤ 6 deep. */
export function reportableValue(value) {
  var json;
  try { json = JSON.stringify(value === undefined ? null : value); } catch (e) { return false; }
  return typeof json === 'string' && json.length <= 2000 && depthOf(value, 0) <= 6;
}

/**
 * One harness result → the check report entry `{ status, value }` for a test. In stdout mode
 * the value is the captured printed output; otherwise the JSON-safe returned value. Anything
 * the server could not compare is reported as `error`.
 */
export function checkEntry(result, testMode) {
  if (!result) { return { status: 'error', value: null }; }
  if (result.status === 'capped') { return { status: 'capped', value: null }; }
  if (result.status !== 'ok') { return { status: 'error', value: null }; }
  if (testMode === 'stdout') {
    if (result.outputTruncated) { return { status: 'error', value: null }; }
    return { status: 'ok', value: String(result.output || '') };
  }
  if (result.valueError) { return { status: 'error', value: null }; }
  var v = result.value === undefined ? null : result.value;
  return reportableValue(v) ? { status: 'ok', value: v } : { status: 'error', value: null };
}

/**
 * Local badges for staff / preview — the same rule the server applies to a student's `check`.
 * `results` = `[{ version, tests:[{status, value}] }]`.
 */
export function localBadges(compare, compareKey, results) {
  var versions = (compare && Array.isArray(compare.versions)) ? compare.versions : [];
  var tests = (compare && Array.isArray(compare.tests)) ? compare.tests : [];
  var expects = (compareKey && Array.isArray(compareKey.expects)) ? compareKey.expects : [];
  var stdout = compare && compare.testMode === 'stdout';
  var list = Array.isArray(results) ? results : [];
  return versions.map(function (v, vi) {
    var entry = list.filter(function (r) { return r && r.version === vi; })[0];
    var reported = (entry && Array.isArray(entry.tests)) ? entry.tests : [];
    var passed = 0;
    tests.forEach(function (t, ti) {
      var r = reported[ti];
      if (!r || r.status !== 'ok' || !reportableValue(r.value)) { return; }
      var want = expects[ti] === undefined ? null : expects[ti];
      var ok = stdout ? stdoutEqual(r.value, want) : valuesEqual(r.value === undefined ? null : r.value, want);
      if (ok) { passed++; }
    });
    return { version: vi, passed: passed, total: tests.length };
  });
}

/** A badge's words — icon + text in the chrome, never colour alone. */
export function badgeText(badge) {
  if (!badge || !badge.total) { return ''; }
  if (badge.passed === badge.total) { return 'Passes all ' + badge.total + (badge.total === 1 ? ' test' : ' tests'); }
  return 'Passes ' + badge.passed + ' of ' + badge.total + (badge.total === 1 ? ' test' : ' tests');
}

// ── metric labels ──────────────────────────────────────────────────────────────────────────

/** The label a version's panel shows: the author's metric, or "Steps = lines run" (fallback). */
export function metricLabelFor(compare, marked) {
  if (!marked) { return 'Steps = lines run'; }
  var label = compare && typeof compare.metricLabel === 'string' ? compare.metricLabel.trim() : '';
  return label ? 'Steps = ' + label : 'Steps = marked lines run';
}

/** True when some versions count marked lines and others count every line. */
export function mixedMetrics(markedFlags) {
  var flags = Array.isArray(markedFlags) ? markedFlags : [];
  return flags.some(Boolean) && flags.some(function (f) { return !f; });
}

export var MIXED_METRIC_NOTICE = 'These versions count different things (some count marked lines, some count every line), so their step counts are not directly comparable.';

// ── the allowed judge choices (mirrors the server) ─────────────────────────────────────────

export function allowedChoices(compare) {
  var labels = ((compare && compare.versions) || []).map(function (v) { return String((v && v.label) || '').trim(); });
  var extra = labels.length >= 3 ? ['All', 'None'] : ['Both', 'Neither'];
  return {
    correctness: labels.concat(extra),
    clarity: labels.concat(['Same']),
    efficiency: labels.concat(['Same']),
    suitability: labels.slice()
  };
}

export function rowResultText(result) {
  if (result === 'match') { return 'Matches the answer key'; }
  if (result === 'mismatch') { return 'Not the keyed answer'; }
  if (result === 'open') { return 'Open question — your reason was recorded'; }
  return '';
}

/** Local judge grade for staff / preview (the server recomputes it for students). */
export function localJudge(compare, compareKey, judge) {
  var answers = (compareKey && compareKey.judgeAnswers) || {};
  var out = { judge: {}, keyedCorrect: 0, keyedTotal: 0 };
  ROWS.forEach(function (row) {
    var j = (judge && judge[row]) || {};
    var key = String(answers[row] || '').trim();
    var keyed = key !== '' && key.toLowerCase() !== 'open';
    var result = keyed ? (j.choice === key ? 'match' : 'mismatch') : 'open';
    if (keyed) { out.keyedTotal++; if (result === 'match') { out.keyedCorrect++; } }
    out.judge[row] = { choice: j.choice || '', reason: j.reason || '', result: result };
  });
  return out;
}

/** Client-side judge form validation (the server enforces the same rules with real 400s). */
export function judgeFormErrors(compare, judge) {
  var allowed = allowedChoices(compare);
  var errs = {};
  ROWS.forEach(function (row) {
    var j = (judge && judge[row]) || {};
    if (!j.choice || allowed[row].indexOf(j.choice) === -1) { errs[row] = 'Choose one answer.'; return; }
    var reason = String(j.reason || '').trim();
    if (!reason) { errs[row] = 'Write a reason for your choice.'; return; }
    if (reason.length > 500) { errs[row] = 'Keep your reason to 500 characters or fewer.'; }
  });
  return errs;
}

// ── state predicates ───────────────────────────────────────────────────────────────────────
// state = { questions, answered, predictionsRecorded, runsCompleted, busy, submitted }

/** Every predict question has a choice (so the predictions can be saved). */
export function predictGateOpen(state) {
  var s = state || {};
  var q = Number(s.questions) || 0;
  return q === 0 || (Number(s.answered) || 0) >= q;
}

/** Run / sweep enabled: no questions, or predictions recorded; and nothing in flight. */
export function runEnabled(state) {
  var s = state || {};
  if (s.busy) { return false; }
  return (Number(s.questions) || 0) === 0 || s.predictionsRecorded === true;
}

/** The judge form is usable: predictions recorded (or none authored) AND ≥1 completed run —
 *  or a submitted response is being shown (read-only). */
export function judgeEnabled(state) {
  var s = state || {};
  if (s.submitted) { return true; }
  var predicted = (Number(s.questions) || 0) === 0 || s.predictionsRecorded === true;
  return predicted && (Number(s.runsCompleted) || 0) >= 1;
}

/** What one version panel shows: 'empty' (Run to see results), 'loading', or 'result'. */
export function panelState(panel) {
  var p = panel || {};
  if (p.loading) { return 'loading'; }
  if (p.result) { return 'result'; }
  return 'empty';
}

// ── the growth framing (plain words, per version) ──────────────────────────────────────────

/**
 * One sentence per version from its steps at the SMALLEST (n1, s1) and LARGEST (n2, s2) sweep
 * sizes. `null` steps = capped ("too many to count"). `kind` 'number' says "the number",
 * otherwise "the list". Returns '' when no sentence applies.
 */
export function growthSentence(n1, s1, n2, s2, kind) {
  var noun = kind === 'number' ? 'the number' : 'the list';
  if (!(n2 > n1)) { return ''; }
  // An errored size is not a measurement at all — never frame it (and never as "capped").
  if (isErrorCell(s1) || isErrorCell(s2)) { return ''; }
  if (s2 === null || s2 === undefined) { return 'grows much faster than ' + noun; }
  if (s1 === null || s1 === undefined) { return ''; }
  if (s1 === 0 || s2 <= s1) { return 'stays about the same however long ' + noun + ' is'; }
  var g = (s2 / s1) / (n2 / n1);
  if (g < 0.5) { return 'grows more slowly than ' + noun; }
  if (g <= 1.5) { return 'grows at about the same rate as ' + noun; }
  if (g < 3) { return 'grows a bit faster than ' + noun; }
  return 'grows much faster than ' + noun;
}

// ── sweep cells: a measurement, "too many to count", or an ERROR (kept apart from capped) ──

/** True for the in-memory error marker `{ error: '<Type: message>' }`. */
export function isErrorCell(v) {
  return !!v && typeof v === 'object' && typeof v.error === 'string';
}

/**
 * One harness result → its in-memory growth-table cell: the integer step count when it ran to
 * the end, `{ error }` when the version RAISED (shown inline, never as "too many to count"),
 * otherwise `null` (capped, timed out, lost — "too many to count").
 */
export function sweepCell(result) {
  if (result && result.status === 'ok' && Number.isInteger(result.steps)) { return result.steps; }
  if (result && result.status === 'error') {
    var msg = typeof result.error === 'string' && result.error.trim() ? result.error.trim() : 'Error';
    return { error: msg };
  }
  return null;
}

/**
 * The step count a cell contributes to the STORED run history. The server whitelist only holds
 * an integer or `null` (ledger J.2), so an errored size is stored as `null` exactly like a capped
 * one; the teacher drill-down therefore labels `null` "no count" (too many steps OR an error),
 * never "too many to count".
 */
export function historySteps(cell) {
  return (typeof cell === 'number' && Number.isInteger(cell)) ? cell : null;
}

/** A growth-table cell's text: the count, "too many to count", the error, or "Not run yet". */
export function growthCellText(v) {
  if (v === undefined) { return 'Not run yet'; }
  if (isErrorCell(v)) { return v.error; }
  return stepsText(v);
}

/**
 * The growth table model: exactly one row per CONFIGURED sweep size (a custom size never adds a
 * row), a cell per version, and one sentence per version. `sweepResults[vi][size]` = steps
 * (integer), null (capped), `{ error }` (the version raised) or undefined (not run yet). An
 * errored cell has NO bar, and a version with ANY errored sweep cell gets NO sentence.
 */
export function growthTable(sweep, versionCount, sweepResults, kind) {
  var sizes = (Array.isArray(sweep) ? sweep : []).filter(function (n) { return Number.isInteger(n) && n > 0; })
    .slice().sort(function (a, b) { return a - b; })
    .filter(function (n, i, arr) { return i === 0 || arr[i - 1] !== n; });
  var res = sweepResults || [];
  var max = 0;
  var rows = sizes.map(function (size) {
    var cells = [];
    for (var vi = 0; vi < versionCount; vi++) {
      var v = res[vi] ? res[vi][size] : undefined;
      if (typeof v === 'number' && v > max) { max = v; }
      cells.push(v);
    }
    return { size: size, cells: cells };
  });
  rows.forEach(function (row) {
    row.bars = row.cells.map(function (v) {
      if (v === null) { return 1; }
      if (typeof v !== 'number' || !max) { return 0; }
      return v / max;
    });
  });
  var sentences = [];
  if (sizes.length >= 2) {
    var n1 = sizes[0], n2 = sizes[sizes.length - 1];
    for (var i = 0; i < versionCount; i++) {
      var a = res[i] ? res[i][n1] : undefined, b = res[i] ? res[i][n2] : undefined;
      if (a === undefined || b === undefined) { sentences.push(''); continue; }
      var errored = sizes.some(function (n) { return isErrorCell(res[i] ? res[i][n] : undefined); });
      if (errored) { sentences.push(''); continue; }
      sentences.push(growthSentence(n1, a, n2, b, kind));
    }
  }
  return { sizes: sizes, rows: rows, sentences: sentences };
}

export function stepsText(steps) {
  if (steps === null) { return TOO_MANY; }
  if (typeof steps !== 'number') { return ''; }
  return steps.toLocaleString('en-US');
}

// code-a-game-browser-fixes (D2/D3): "1 step", "2 steps", "1,000 steps". null returns EXACTLY what
// stepsText(null) returns (no " steps" on the capped phrase); a non-number is ''.
export function stepsLabel(steps) {
  if (steps === null) { return stepsText(null); }
  if (typeof steps !== 'number') { return ''; }
  return stepsText(steps) + (steps === 1 ? ' step' : ' steps');
}

// A size (n) for display: grouped like the growth-table cells. The INPUT value stays unformatted.
export function sizeText(n) {
  return typeof n === 'number' && isFinite(n) ? n.toLocaleString('en-US') : String(n);
}

// ── timeout salvage (the chrome only executes what this decides) ───────────────────────────

/**
 * items     the item ids of this operation, smallest first
 * finished  the ids that already have a result (ok, error or capped)
 * elapsedMs the salvage time used so far
 * lastResult { type: 'run-all' | 'rerun', item?, status } — what just happened
 *
 * Returns { type: 'done' } | { type: 'rerun', item } | { type: 'mark-capped', items }:
 *   * nothing unfinished → done;
 *   * the 30 s salvage budget is spent → mark every unfinished item capped;
 *   * a SOLO re-run that did not finish its item (timeout, lost, error or no result line) →
 *     mark that item AND every larger unfinished item capped, without re-running them;
 *   * otherwise → re-run the next unfinished item ALONE.
 */
export function nextSalvageAction(input) {
  var o = input || {};
  var items = Array.isArray(o.items) ? o.items : [];
  var finished = Array.isArray(o.finished) ? o.finished : [];
  var unfinished = items.filter(function (id) { return finished.indexOf(id) === -1; });
  if (!unfinished.length) { return { type: 'done' }; }
  if ((Number(o.elapsedMs) || 0) >= SALVAGE_BUDGET_MS) { return { type: 'mark-capped', items: unfinished }; }
  var last = o.lastResult || {};
  if (last.type === 'rerun' && unfinished.indexOf(last.item) !== -1) {
    var at = items.indexOf(last.item);
    return { type: 'mark-capped', items: unfinished.filter(function (id) { return items.indexOf(id) >= at; }) };
  }
  return { type: 'rerun', item: unfinished[0] };
}
